import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';

export const MCP_OAUTH_SCOPES = [
  { code: 'CLIENT_VIEW', label: 'Odczyt klientów' },
  { code: 'CLIENT_EDIT', label: 'Dodawanie i edycja klientów', requires: 'CLIENT_VIEW' },
  { code: 'INTAKE_VIEW', label: 'Odczyt ujęć' },
  { code: 'INTAKE_CREATE', label: 'Dodawanie ujęć', requires: 'INTAKE_VIEW' },
  { code: 'INTAKE_EDIT', label: 'Edycja ujęć', requires: 'INTAKE_VIEW' },
  { code: 'CONTRACT_VIEW', label: 'Odczyt umów' },
  { code: 'CONTRACT_CREATE', label: 'Dodawanie umów', requires: 'CONTRACT_VIEW' },
  { code: 'CONTRACT_EDIT', label: 'Edycja szkiców umów', requires: 'CONTRACT_VIEW' },
  { code: 'OFFER_VIEW', label: 'Odczyt ofert' },
  { code: 'OFFER_CREATE', label: 'Dodawanie ofert', requires: 'OFFER_VIEW' },
  { code: 'OFFER_EDIT', label: 'Edycja ofert i ich statusu', requires: 'OFFER_VIEW' },
  { code: 'STUDY_VIEW', label: 'Odczyt opracowań' },
  { code: 'STUDY_CREATE', label: 'Dodawanie opracowań', requires: 'STUDY_VIEW' },
  { code: 'STUDY_EDIT', label: 'Edycja opracowań (bez statusu i kroków)', requires: 'STUDY_VIEW' },
] as const;

const MCP_SCOPE_REGISTERS: Record<string, string> = { CLIENT: 'Klienci', INTAKE: 'Ujęcia', CONTRACT: 'Umowy', OFFER: 'Oferty', STUDY: 'Opracowania' };
const MCP_SCOPE_ACTIONS: Record<string, string> = { VIEW: 'odczyt', CREATE: 'dodawanie', EDIT: 'edycja' };
const scopeRegister = (code: string) => code.slice(0, code.lastIndexOf('_'));

/**
 * Zakresy, które użytkownik może dziś przyznać, a połączenie lub token ich nie ma (np. zatwierdzone przed dodaniem
 * nowych narzędzi), jako „Oferty, Umowy (edycja)”; null, gdy niczego nie brakuje.
 */
export function missingMcpScopesLabel(granted: string[], permissions: string[] | undefined): string | null {
  const has = new Set(permissions ?? []);
  const grantable = MCP_OAUTH_SCOPES.filter(scope => has.has(scope.code) && (!('requires' in scope) || has.has(scope.requires)));
  const parts = [...new Set(grantable.map(scope => scopeRegister(scope.code)))].flatMap(register => {
    const own = grantable.filter(scope => scopeRegister(scope.code) === register);
    const missing = own.filter(scope => !granted.includes(scope.code));
    const label = MCP_SCOPE_REGISTERS[register] ?? register;
    if (!missing.length) return [];
    return missing.length === own.length ? [label] : [`${label} (${missing
      .map(scope => MCP_SCOPE_ACTIONS[scope.code.slice(register.length + 1)] ?? scope.label).join(', ')})`];
  });
  return parts.length ? parts.join(', ') : null;
}

export function toggleMcpScope(current: string[], code: string, checked: boolean) {
  const next = new Set(current);
  if (checked) {
    next.add(code);
    const scope = MCP_OAUTH_SCOPES.find(item => item.code === code);
    if (scope && 'requires' in scope) next.add(scope.requires);
  } else {
    next.delete(code);
    MCP_OAUTH_SCOPES.forEach(scope => {
      if ('requires' in scope && scope.requires === code) next.delete(scope.code);
    });
  }
  return [...next];
}

interface OAuthClient {
  id: string | number;
  clientId: string;
  name: string;
  scopes: string[];
  createdAt: string;
  revokedAt: string | null;
}
interface OAuthConfig {
  serverUrl: string;
  issuer: string;
  redirectUri: string;
  publishedClientId: string;
  publishedIdentitySupported: boolean;
}
interface OAuthGrant {
  id: string;
  name: string;
  clientId: string;
  clientKind: 'CLAUDE_PUBLISHED' | 'PERSONAL';
  scopes: string[];
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
}
interface IssuedClient { client: OAuthClient; clientSecret: string }
const ENDPOINT = '/api/auth/mcp-oauth';

export function McpOAuthCard() {
  const { user, impersonation } = useAuth();
  const generation = useRef(0);
  const [config, setConfig] = useState<OAuthConfig | null>(null);
  const [clients, setClients] = useState<OAuthClient[]>([]);
  const [grants, setGrants] = useState<OAuthGrant[]>([]);
  const [issued, setIssued] = useState<IssuedClient | null>(null);
  const [name, setName] = useState('Claude');
  const [scopes, setScopes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [revokeId, setRevokeId] = useState<OAuthClient['id'] | null>(null);
  const [revokeGrantId, setRevokeGrantId] = useState<string | null>(null);

  useEffect(() => {
    const current = ++generation.current;
    setIssued(null); setClients([]); setGrants([]); setConfig(null); setScopes([]);
    setError(null); setCopied(null); setBusy(false); setRevokeId(null); setRevokeGrantId(null);
    if (!user || impersonation) { setLoading(false); return; }
    setLoading(true);
    Promise.all([api.get<OAuthConfig>(`${ENDPOINT}/config`), api.get<OAuthClient[]>(`${ENDPOINT}/clients`),
      api.get<OAuthGrant[]>(`${ENDPOINT}/grants`)])
      .then(([settings, items, connections]) => {
        if (generation.current === current) { setConfig(settings); setClients(items); setGrants(connections); }
      })
      .catch(err => {
        if (generation.current === current) setError(err instanceof Error ? err.message : 'Nie udało się pobrać połączeń OAuth.');
      })
      .finally(() => { if (generation.current === current) setLoading(false); });
    return () => { generation.current++; };
  }, [user?.id, impersonation]);

  async function copy(value: string, key: string) {
    const current = generation.current;
    try {
      await navigator.clipboard.writeText(value);
      if (generation.current === current) setCopied(key);
    } catch {
      if (generation.current === current) setError('Zaznacz i skopiuj wartość z pola. Schowek jest niedostępny.');
    }
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    const current = generation.current;
    setBusy(true); setError(null); setCopied(null);
    try {
      const result = await api.post<IssuedClient>(`${ENDPOINT}/clients`, { name, scopes });
      if (generation.current !== current) return;
      setIssued(result);
      setClients(items => [result.client, ...items]);
    } catch (err) {
      if (generation.current === current) setError(err instanceof Error ? err.message : 'Nie udało się utworzyć klienta OAuth.');
    } finally { if (generation.current === current) setBusy(false); }
  }

  async function revoke(id: OAuthClient['id']) {
    const current = generation.current;
    setBusy(true); setError(null);
    try {
      await api.del(`${ENDPOINT}/clients/${encodeURIComponent(String(id))}`);
      if (generation.current !== current) return;
      setClients(items => items.map(client => client.id === id ? { ...client, revokedAt: new Date().toISOString() } : client));
      const clientId = clients.find(client => client.id === id)?.clientId;
      setGrants(items => items.map(grant => grant.clientId === clientId ? { ...grant, revokedAt: new Date().toISOString() } : grant));
      if (issued?.client.id === id) setIssued(null);
      setRevokeId(null);
    } catch (err) {
      if (generation.current === current) setError(err instanceof Error ? err.message : 'Nie udało się unieważnić połączenia.');
    } finally { if (generation.current === current) setBusy(false); }
  }

  async function refreshGrants() {
    const current = generation.current;
    setBusy(true); setError(null);
    try {
      const items = await api.get<OAuthGrant[]>(`${ENDPOINT}/grants`);
      if (generation.current === current) setGrants(items);
    } catch (err) {
      if (generation.current === current) setError(err instanceof Error ? err.message : 'Nie udało się odświeżyć listy połączeń.');
    } finally { if (generation.current === current) setBusy(false); }
  }

  async function revokeGrant(id: string) {
    const current = generation.current;
    setBusy(true); setError(null);
    try {
      await api.del(`${ENDPOINT}/grants/${encodeURIComponent(id)}`);
      if (generation.current !== current) return;
      setGrants(items => items.map(grant => grant.id === id ? { ...grant, revokedAt: new Date().toISOString() } : grant));
      setRevokeGrantId(null);
    } catch (err) {
      if (generation.current === current) setError(err instanceof Error ? err.message : 'Nie udało się cofnąć dostępu.');
    } finally { if (generation.current === current) setBusy(false); }
  }

  function field(label: string, value: string, key: string, secret = false) {
    return <div className="space-y-2">
      <label className="field"><span className="field-label">{label}</span>
        <input className="input font-mono text-xs" type={secret ? 'password' : 'text'} value={value}
          readOnly autoComplete="off" onFocus={event => event.currentTarget.select()} /></label>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => void copy(value, key)}>
        {copied === key ? 'Skopiowano' : `Kopiuj ${label}`}</button>
    </div>;
  }

  return <section className="card mb-6" aria-labelledby="mcp-oauth-heading">
    <div className="card-head"><h2 className="card-title" id="mcp-oauth-heading">Claude - połączenie OAuth</h2></div>
    <div className="card-body space-y-4 text-[13px]">
      <p className="text-ink-600">Podłącz portal w aplikacji Claude. Podczas logowania wybierzesz zakres dostępu do klientów, ujęć, ofert, umów i opracowań.
        Claude będzie działać z Twoimi aktualnymi uprawnieniami w portalu.</p>
      {impersonation ? <p>Wróć na własne konto, aby skonfigurować OAuth.</p> : <>
        {error && <p role="alert" className="p-3 bg-red-100 text-red-500 rounded-md">{error}</p>}
        {loading && <p>Ładowanie konfiguracji…</p>}
        {config && <div className="rounded-md border border-ink-100 p-3 space-y-3">
          {field('Remote MCP server URL', config.serverUrl, 'url')}
        </div>}
        {config?.publishedIdentitySupported && <div className="rounded-md border border-ink-100 p-3 space-y-2">
          <h3 className="font-semibold">Podłącz Claude przez logowanie OAuth</h3>
          <ol className="list-decimal pl-5 space-y-1">
            <li>W Claude otwórz Connectors → Add custom connector. Wpisz nazwę oraz skopiowany Remote MCP server URL.</li>
            <li>W sekcji Authentication wybierz „Sign in now”, a w OAuth client wybierz „Use Claude’s published identity”.</li>
            <li>Kliknij Add, potem Connect. Zaloguj się do portalu i zatwierdź wybrane uprawnienia.</li>
          </ol>
          <p>Client ID i Client Secret są zbędne przy tej opcji.</p>
          <p className="text-ink-500">Jeśli zapisany konektor nie pozwala zmienić sposobu połączenia, usuń go i dodaj ponownie.</p>
        </div>}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="font-semibold">Dostęp przyznany przez Ciebie</h3>
            <button className="btn btn-secondary btn-sm" disabled={busy || loading} onClick={() => void refreshGrants()}>Odśwież połączenia</button>
          </div>
          {!loading && grants.length === 0 && <p className="text-ink-500">Po zatwierdzeniu połączenia w Claude pojawi się tutaj udzielony dostęp.</p>}
          <ul className="space-y-3">{grants.map(grant => {
            const expired = new Date(grant.expiresAt).getTime() <= Date.now();
            const missing = grant.revokedAt || expired ? null : missingMcpScopesLabel(grant.scopes, user?.permissions);
            return <li key={grant.id} className="rounded-md border border-ink-100 p-3 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div><h4 className="font-semibold">{grant.name}</h4>
                  <p className="text-ink-500">{grant.revokedAt ? 'Dostęp cofnięty' : expired ? 'Dostęp wygasł' : 'Aktywne połączenie'}</p></div>
                {!grant.revokedAt && !expired && (revokeGrantId === grant.id ? <div className="flex gap-2">
                  <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => void revokeGrant(grant.id)}>Potwierdź cofnięcie dostępu</button>
                  <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setRevokeGrantId(null)}>Anuluj</button>
                </div> : <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setRevokeGrantId(grant.id)}>Cofnij dostęp</button>)}
              </div>
              <p className="text-ink-500">{grant.scopes.map(code => MCP_OAUTH_SCOPES.find(scope => scope.code === code)?.label ?? code).join(', ')}</p>
              {missing && <p className="text-amber-800">Nie obejmuje: {missing} – {grant.clientKind === 'PERSONAL'
                ? 'utwórz nowego klienta OAuth z tymi zakresami i połącz ponownie'
                : 'połącz konektor ponownie, aby je dodać, a potem cofnij to połączenie'}.</p>}
              <p className="text-ink-500">Przyznano: {new Date(grant.createdAt).toLocaleString('pl-PL')}. Ważny do: {new Date(grant.expiresAt).toLocaleString('pl-PL')}.</p>
              {revokeGrantId === grant.id && <p>Claude utraci dostęp z tego połączenia. Ponowne połączenie wymaga Twojego logowania i zgody.</p>}
            </li>;
          })}</ul>
        </div>
        <details className="rounded-md border border-ink-100 p-3" open={config && !config.publishedIdentitySupported ? true : undefined}>
          <summary className="cursor-pointer font-semibold">Własny klient OAuth - konfiguracja ręczna</summary>
          <div className="space-y-4 mt-4">
        <p>Ta sekcja służy do połączeń z opcją „Use your own OAuth client”. Wygeneruj dane klienta i wpisz je w Claude.</p>
        {issued && <div className="rounded-md border border-water-200 bg-water-50/30 p-3 space-y-4" role="status">
          <h3 className="font-semibold">Dane klienta „{issued.client.name}”</h3>
          <p>Wklej poniższe wartości w Claude. Client Secret pokazujemy tylko teraz; przechowuj go prywatnie.</p>
          {field('OAuth Client ID', issued.client.clientId, 'new-client')}
          {field('OAuth Client Secret', issued.clientSecret, 'new-secret', true)}
          <button className="btn btn-secondary btn-sm" onClick={() => setIssued(null)}>Zapisano - ukryj sekret</button>
        </div>}
        <form onSubmit={event => void create(event)} className="space-y-3">
          <label className="field"><span className="field-label">Nazwa połączenia</span>
            <input className="input" value={name} onChange={event => setName(event.target.value)} maxLength={80}
              placeholder="np. Claude" required disabled={busy} /></label>
          <fieldset><legend className="field-label mb-2">Dostęp, który możesz przyznać Claude</legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{MCP_OAUTH_SCOPES.map(scope => {
              const available = user?.permissions?.includes(scope.code)
                && (!('requires' in scope) || user?.permissions?.includes(scope.requires));
              return <label key={scope.code} className={`flex items-center gap-2 ${available ? '' : 'text-ink-400'}`}>
                <input type="checkbox" disabled={!available || busy} checked={scopes.includes(scope.code)}
                  onChange={event => setScopes(current => toggleMcpScope(current, scope.code, event.target.checked))} />
                {scope.label}{!available && ' (brak uprawnienia)'}
              </label>;
            })}</div>
            <p className="mt-2 text-ink-500">Przy dodawaniu ujęć wybierz również odczyt klientów, aby Claude mógł przypisać ujęcie do klienta.</p>
          </fieldset>
          <button className="btn btn-primary btn-sm" disabled={busy || loading || !config || !name.trim() || !scopes.length || !!issued}>
            {busy ? 'Zapisuję…' : 'Utwórz klienta OAuth'}</button>
        </form>
        <div className="rounded-md border border-ink-100 p-3 space-y-2">
          <h3 className="font-semibold">Dokończ połączenie w Claude</h3>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Otwórz Connectors → Add custom connector. Wpisz nazwę i skopiowany Remote MCP server URL.</li>
            <li>Włącz „Use your own OAuth client” i wklej OAuth Client ID oraz OAuth Client Secret.</li>
            <li>Kliknij Add, a następnie Connect. Zaloguj się do portalu i zatwierdź wybrane uprawnienia.</li>
          </ol>
          <p className="text-ink-500">Jeśli zapisany konektor nie pozwala zmienić ustawień, usuń go i dodaj ponownie z tymi danymi.</p>
        </div>
        <div className="space-y-3"><h3 className="font-semibold">Twoi klienci OAuth</h3>
          {!loading && clients.length === 0 && <p className="text-ink-500">Nie masz jeszcze klientów OAuth.</p>}
          <ul className="space-y-3">{clients.map(client => <li key={client.id} className="rounded-md border border-ink-100 p-3 space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div><h4 className="font-semibold">{client.name}</h4>
                <p className="text-ink-500">{client.revokedAt ? 'Unieważniony' : 'Aktywny'}</p></div>
              {!client.revokedAt && (revokeId === client.id ? <div className="flex gap-2">
                <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => void revoke(client.id)}>Potwierdź unieważnienie</button>
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setRevokeId(null)}>Anuluj</button>
              </div> : <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => setRevokeId(client.id)}>Unieważnij</button>)}
            </div>
            <p className="break-all font-mono text-xs">Client ID: {client.clientId}</p>
            <p className="text-ink-500">{client.scopes.map(code => MCP_OAUTH_SCOPES.find(scope => scope.code === code)?.label ?? code).join(', ')}</p>
            {revokeId === client.id && <p>Claude utraci dostęp przez tego klienta. Ponowne podłączenie wymaga utworzenia nowego klienta OAuth.</p>}
          </li>)}</ul>
        </div>
          </div>
        </details>
      </>}
    </div>
  </section>;
}
